import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./Subscription.css";
import useHttp from "../../hooks/use-http";

export default function Subscription() {
  const navigate = useNavigate();
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [customError, setCustomError] = useState("");

  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [hasStartedSession, setHasStartedSession] = useState(false);

  const [sessionToken, setSessionToken] = useState("");
  const [plan, setPlan] = useState(null);
  const [merchant, setMerchant] = useState(null);

  const [isAuthorized, setIsAuthorized] = useState(false);
  const [cardNumber, setCardNumber] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [cvv, setCvv] = useState("");
  const [cardHolderName, setCardHolderName] = useState("");

  const { isLoading: isSessionReqLoading, error: sessionReqError, sendRequest: fetchSessionReq } = useHttp();
  const { isLoading: isEnrolling, error: enrollReqError, sendRequest: enrollReq } = useHttp();

  const query = useMemo(() => new URLSearchParams(window.location.search), []);
  const planId = query.get("id");
  const initialEmail = query.get("customerEmail") || query.get("email") || "";

  const [email, setEmail] = useState(initialEmail);

  const isLoadingSession = (isInitialLoading && hasStartedSession) || isSessionReqLoading;
  const displayError = customError || sessionReqError || enrollReqError || "";

  const formatMoney = (amount, currency) => {
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount)) return "";
    try {
      return new Intl.NumberFormat("en-NG", {
        style: "currency",
        currency: currency || "NGN",
        minimumFractionDigits: 2,
      }).format(numericAmount);
    } catch (e) {
      return `${numericAmount}`;
    }
  };

  const processSessionData = useCallback((data) => {
    setIsInitialLoading(false);
    if (!data?.success) {
      setCustomError(
        data?.title || data?.message || "Failed to create checkout session"
      );
      return;
    }

    setSessionToken(data?.sessionToken || "");
    setPlan(data?.plan || null);
    setMerchant(data?.merchant || null);
  }, []);

  const handleStartSession = (e) => {
    e?.preventDefault?.();
    setCustomError("");

    if (!planId) {
      setCustomError("Missing required query params: id");
      setIsInitialLoading(false);
      return;
    }

    if (!email) {
      setCustomError("Please enter your email address to proceed");
      return;
    }

    setHasStartedSession(true);
    setIsInitialLoading(true);

    const payload = {
      publicId: planId,
      customerEmail: email,
    };

    fetchSessionReq(
      {
        url: `${process.env.REACT_APP_BASE_URL}api/checkout-sessions`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: payload,
      },
      processSessionData
    );
  };

  useEffect(() => {
    if (initialEmail && planId) {
      handleStartSession();
    } else {
      setIsInitialLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (sessionReqError) {
      setIsInitialLoading(false);
    }
  }, [sessionReqError]);

  const handleCardNumberChange = (e) => {
    let val = e.target.value
      .replace(/[^\d]/g, "")
      .replace(/(.{4})/g, "$1 ")
      .trim();
    setCardNumber(val);
  };

  const handleExpiryDateChange = (e) => {
    let val = e.target.value.replace(/[^\d/]/g, "");
    if (val.length === 2 && !val.includes("/")) {
      val = `${val}/`;
    }
    setExpiryDate(val);
  };

  const handleAuthChange = (e) => {
    setIsAuthorized(e.target.checked);
  };

  const processEnrollData = useCallback((data) => {
    if (!data?.success) {
      setCustomError(data?.title || data?.message || "Enrollment failed");
      return;
    }

    if (data?.checkoutUrl) {
      window.location.replace(data.checkoutUrl);
      return;
    }

    setIsSuccessModalOpen(true);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCustomError("");

    if (!sessionToken) {
      setCustomError("Session token is missing. Please refresh and try again.");
      return;
    }

    const payload = {
      sessionToken,
      cardNumber: cardNumber.replace(/\s/g, ""),
      expiryDate,
      cvv,
      cardHolderName,
      isAuthorized,
      email,
    };

    enrollReq(
      {
        url: `${process.env.REACT_APP_BASE_URL}api/subscription/enroll-with-session`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: payload,
      },
      processEnrollData
    );
  };

  return (
    <div className="checkout-container">
      <div className="checkout-card">
        <div className="plan-summary">
          <div className="plan-name">
            {isLoadingSession ? "Loading..." : plan?.name || "Subscription"}
          </div>
          <div className="plan-price">
            {isLoadingSession
              ? ""
              : formatMoney(plan?.amount, plan?.currency) || ""}
          </div>
          <div className="plan-interval">
            {isLoadingSession
              ? ""
              : `${plan?.frequency || ""}${merchant?.name ? ` • ${merchant.name}` : ""}`}
          </div>
        </div>

        {displayError ? (
          <div className="error-message" style={{ marginBottom: 12 }}>
            {displayError}
          </div>
        ) : null}

        {!hasStartedSession || (!sessionToken && !isLoadingSession) ? (
          <form className="form-body" onSubmit={handleStartSession}>
            <div className="input-group">
              <label>EMAIL ADDRESS</label>
              <input
                type="email"
                placeholder="example@mail.com"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoadingSession}
              />
            </div>
            <button
              type="submit"
              className="btn-submit"
              disabled={isLoadingSession || !email}
            >
              {isLoadingSession ? "Loading..." : "Proceed"}
            </button>
          </form>
        ) : !isLoadingSession && (!sessionToken || displayError) ? null : (
          <form className="form-body" onSubmit={handleSubmit}>
            <div className="input-group">
              <label>CARDHOLDER NAME</label>
              <input
                type="text"
                placeholder="John Doe"
                required
                value={cardHolderName}
                onChange={(e) => setCardHolderName(e.target.value)}
                disabled={isLoadingSession || isEnrolling}
              />
            </div>

            <div className="input-group">
              <label>CARD NUMBER</label>
              <input
                type="text"
                id="cardNo"
                placeholder="0000 0000 0000 0000"
                maxLength="19"
                required
                value={cardNumber}
                onChange={handleCardNumberChange}
                disabled={isLoadingSession || isEnrolling}
              />
            </div>

            <div className="row">
              <div className="input-group" style={{ flex: 2 }}>
                <label>EXPIRY DATE</label>
                <input
                  type="text"
                  placeholder="MM/YY"
                  maxLength="5"
                  required
                  value={expiryDate}
                  onChange={handleExpiryDateChange}
                  disabled={isLoadingSession || isEnrolling}
                />
              </div>
              <div className="input-group" style={{ flex: 1 }}>
                <label>CVV</label>
                <input
                  type="password"
                  placeholder="***"
                  maxLength="3"
                  required
                  value={cvv}
                  onChange={(e) => setCvv(e.target.value.replace(/[^\d]/g, ""))}
                  disabled={isLoadingSession || isEnrolling}
                />
              </div>
            </div>

            <div className="auth-box">
              <input
                type="checkbox"
                id="authCheck"
                required
                checked={isAuthorized}
                onChange={handleAuthChange}
                disabled={isLoadingSession || isEnrolling}
              />
              <div className="auth-text">
                I authorize <strong>Payloft</strong> to securely store my card
                details and automatically debit{" "}
                <strong>
                  {formatMoney(plan?.amount, plan?.currency) ||
                    "your subscription"}
                </strong>{" "}
                from this card every{" "}
                <strong>{(plan?.frequency || "period").toLowerCase()}</strong>{" "}
                until I cancel this subscription.
              </div>
            </div>

            <button
              type="submit"
              className="btn-submit"
              id="submitBtn"
              disabled={!isAuthorized || isLoadingSession || isEnrolling}
            >
              {isEnrolling ? "Processing Authorization..." : "Activate Subscription"}
            </button>

            <div className="secure-footer">
              🔒 Secured by Payloft Tokenization Services
            </div>
          </form>
        )}
      </div>

      {isSuccessModalOpen ? (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex justify-center items-center">
          <div className="modal-content w-[420px] bg-white p-8 rounded-[20px] shadow-2xl relative h-fit max-h-[95vh] overflow-y-auto">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 bg-emerald-50 border border-emerald-100 rounded-full flex items-center justify-center mb-6">
                <span className="text-emerald-600 text-2xl font-bold">✓</span>
              </div>

              <h3 className="text-xl text-navy-blue font-bold mb-2">
                Subscription Activated
              </h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-8">
                Your subscription has been successfully activated.
              </p>

              <button
                onClick={() => navigate("/")}
                className="w-full h-[50px] rounded-xl font-bold bg-primary text-white hover:bg-opacity-90 transition-all"
              >
                Complete
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
